import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/base/SeoHead';
import { useState } from 'react';
import { categories, getArticlesByCategory } from '@/mocks/articles';

const categoryColors: Record<string, { badge: string; iconBg: string; text: string }> = {
  primary: { badge: 'bg-primary-100 text-primary-700', iconBg: 'bg-primary-100', text: 'text-primary-600' },
  accent: { badge: 'bg-accent-100 text-accent-700', iconBg: 'bg-accent-100', text: 'text-accent-600' },
  secondary: { badge: 'bg-secondary-100 text-secondary-700', iconBg: 'bg-secondary-100', text: 'text-secondary-600' },
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const activeCategory = categories.find((c) => c.slug === slug) || categories[0];
  const articles = getArticlesByCategory(activeCategory.slug);
  const colors = categoryColors[activeCategory.color] || categoryColors.primary;

  return (
    <>
      <SeoHead
        title={`${activeCategory.name}——第一次的人妻 | 新婚生活全面指南`}
        description={activeCategory.description}
        keywords={`第一次的人妻,${activeCategory.name},新婚指南,婚姻经营,夫妻相处`}
        canonicalUrl={`/category/${activeCategory.slug}`}
      />
      <Navbar />
      <main className="min-h-screen pt-20">
        <section className="w-full py-12 md:py-16 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8">
              <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 font-medium">{activeCategory.name}</span>
            </nav>

            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 rounded-xl ${colors.iconBg} flex items-center justify-center`}>
                <i className={`${activeCategory.icon} ${colors.text} text-lg`}></i>
              </div>
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-900">{activeCategory.name}</h1>
            </div>
            <p className="text-foreground-600 text-sm md:text-base ml-13 mb-8">{activeCategory.description}</p>

            <div className="flex flex-wrap gap-2 mb-10">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                    cat.slug === activeCategory.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {articles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-white rounded-xl overflow-hidden border border-background-200 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="relative w-full h-48 overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4 md:p-5">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-3 ${colors.badge}`}>
                      {article.categoryName}
                    </span>
                    <h2 className="font-heading text-lg text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h2>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-4">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-500">
                      <span>{article.author}</span>
                      <div className="flex items-center gap-3">
                        <span><i className="ri-time-line mr-1"></i>{article.readTime}</span>
                        <span>{article.publishedAt}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${activeCategory.name}——第一次的人妻`,
            description: activeCategory.description,
            url: `https://diyiciderenqi.com/category/${activeCategory.slug}`,
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://diyiciderenqi.com/' },
                { '@type': 'ListItem', position: 2, name: activeCategory.name, item: `https://diyiciderenqi.com/category/${activeCategory.slug}` },
              ],
            },
          }),
        }}
      />
    </>
  );
}